import { FC, createContext, useContext } from 'react';
import noop from 'utils/noop';
import { RestQueryHookResult } from 'utils/hooks/api/useRestQuery';
import { useBehavioralInsightsQuery, useListId } from '../../hooks';
import { useInsightsBoundaryContext } from '../insightsBoundary';
import { useListDetailsContext } from '../../contexts/listDetails';
import { BehaviorMetric, BehaviorQuery, InsightQueryRequest, RawBehaviorQuery } from '../../types';

type BehaviorRefetch = RestQueryHookResult<RawBehaviorQuery>['refetch'];

export type BehaviorQueryState = BehaviorQuery & {
  behaviorError: unknown;
  isBehaviorError: boolean;
  isBehaviorLoading: boolean;
  behavior: BehaviorQuery;
  refetchBehavior: BehaviorRefetch;
};

const defaultMetric: BehaviorMetric = { value: 0 };

export const behaviorQueryDefaultState = {
  isBehaviorError: false,
  behaviorError: null,
  isBehaviorLoading: false,
  behavior: {},
  totalPurchases: defaultMetric,
  conversionRate: defaultMetric,
  unsubscribeRate: defaultMetric,
  uniquePurchases: defaultMetric,
  revenue: defaultMetric,
  avgOrderValue: defaultMetric,
  refetchBehavior: noop as BehaviorRefetch,
};

export const BehaviorQueryContext = createContext<BehaviorQueryState>(behaviorQueryDefaultState);

export const useBehaviorQueryContext = () => useContext(BehaviorQueryContext);

export const BehaviorQueryContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const listId = useListId(); // Changed to not pull from Angular
  // get the saved query from the details, not the live query (only required/used for Dynamic lists)
  const { searchQuery } = useListDetailsContext();
  const { startDate, endDate } = useInsightsBoundaryContext();

  const insightQueryParam: InsightQueryRequest = {
    emailListId: listId,
    searchQuery,
    startDate: startDate?.toISOString(),
    endDate: endDate?.toISOString(),
  };

  const {
    data: behavior,
    error: behaviorError,
    isError: isBehaviorError,
    isLoading: isBehaviorLoading,
    refetch: refetchBehavior,
  } = useBehavioralInsightsQuery(insightQueryParam);

  const context: BehaviorQueryState = {
    isBehaviorError,
    behaviorError,
    isBehaviorLoading,
    refetchBehavior,
    behavior,
    ...behavior,
  };

  return <BehaviorQueryContext.Provider value={context}>{children}</BehaviorQueryContext.Provider>;
};
